import { useEffect, useState } from 'react'
import { collection, query, where, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/store/authStore'
import { Booking } from '@/types'
import { Check, X, CreditCard, Eye, Info } from 'lucide-react'
import toast from 'react-hot-toast'

interface PaymentQueuePageProps {
    embedded?: boolean
}

export default function PaymentQueuePage({ embedded = false }: PaymentQueuePageProps) {
    const { currentOrg } = useAuthStore()
    const [bookings, setBookings] = useState<Booking[]>([])
    const [loading, setLoading] = useState(true)
    const [selectedReceipt, setSelectedReceipt] = useState<string | null>(null)

    useEffect(() => {
        if (!currentOrg) return

        // Buscar todas as reservas com pagamento pendente ou aprovado
        const q = query(
            collection(db, 'bookings'),
            where('orgId', '==', currentOrg.id),
            where('status', 'in', ['PENDING_PAYMENT', 'CONFIRMED', 'PENDING_APPROVAL'])
        )

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Booking))
            // Filtrar apenas os que têm informação de pagamento
            const withPayment = data.filter(b => b.payment || b.receiptSubmitted)
            // Ordenação por data de atualização
            withPayment.sort((a, b) => {
                const dateA = a.updatedAt?.toDate?.() || new Date(0);
                const dateB = b.updatedAt?.toDate?.() || new Date(0);
                return dateB.getTime() - dateA.getTime();
            });
            setBookings(withPayment)
            setLoading(false)
        })

        return () => unsubscribe()
    }, [currentOrg?.id])

    const handleAction = async (bookingId: string, status: 'CONFIRMED' | 'CANCELED') => {
        try {
            const docRef = doc(db, 'bookings', bookingId)
            await updateDoc(docRef, {
                status,
                paymentStatus: status === 'CONFIRMED' ? 'APPROVED' : 'REJECTED',
                updatedAt: serverTimestamp()
            })
            toast.success(status === 'CONFIRMED' ? 'Pagamento aprovado!' : 'Pagamento rejeitado.')
        } catch (error) {
            console.error('Erro ao processar pagamento:', error)
            toast.error('Erro ao processar ação.')
        }
    }

    if (loading) return <div className="py-20 text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div></div>

    const Content = (
        <div className={`w-full mx-auto ${embedded ? '' : 'p-6 md:p-12 space-y-8 page-enter'}`}>
            {/* Header Section */}
            {!embedded && (
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 pb-12 border-b-2 border-primary/5">
                    <div className="space-y-4">
                        <div className="flex items-center space-x-3">
                        </div>
                        <h1 className="text-3xl md:text-4xl font-black text-primary tracking-tighter uppercase italic leading-[0.9] drop-shadow-sm">Fila de Pagamentos</h1>
                        <p className="premium-text max-w-2xl italic">
                            Valide os comprovantes de Pix enviados pelos membros de <span className="text-primary font-bold">{currentOrg?.name}</span>.
                        </p>
                    </div>
                </div>
            )}


            {/* ... Content items ... */}
            {bookings.length === 0 ? (
                <div className="glass-card py-20 text-center">
                    <CreditCard className="w-16 h-16 mx-auto text-secondary mb-6" />
                    <h3 className="text-xl font-black text-primary uppercase italic">Sem pagamentos pendentes</h3>
                    <p className="premium-text mt-2">Nenhum membro enviou comprovante para validação no momento.</p>
                </div>
            ) : (
                <div className="grid gap-6">
                    {bookings.map((booking) => {
                        const updatedAt = booking.updatedAt?.toDate?.() || new Date()
                        const paymentProvider = booking.payment?.provider || 'pix_manual'
                        const paymentMethod = booking.payment?.method || 'pix'
                        const paymentStatus = booking.payment?.status || 'pending'
                        const transactionId = booking.payment?.mercadoPagoPaymentId || booking.payment?.infinityPayChargeId || 'N/A'

                        // Mapear provider para nome amigável
                        const providerName = {
                            'mercadopago': 'Mercado Pago',
                            'infinitypay': 'InfinityPay',
                            'pix_manual': 'PIX Manual'
                        }[paymentProvider] || 'Desconhecido'

                        // Mapear método para nome amigável
                        const methodName = {
                            'pix': 'PIX',
                            'credit_card': 'Cartão de Crédito',
                            'debit_card': 'Cartão de Débito',
                            'other': 'Outro'
                        }[paymentMethod] || 'Desconhecido'

                        // Cor do status
                        const statusColor = {
                            'pending': 'bg-yellow-100 text-yellow-800',
                            'approved': 'bg-green-100 text-green-800',
                            'paid': 'bg-green-100 text-green-800', // Adicionado
                            'rejected': 'bg-red-100 text-red-800',
                            'cancelled': 'bg-gray-100 text-gray-800'
                        }[paymentStatus] || 'bg-gray-100 text-gray-800'

                        const statusText = {
                            'pending': 'Pendente',
                            'approved': 'Aprovado',
                            'paid': 'Pago/Aprovado', // Adicionado
                            'rejected': 'Rejeitado',
                            'cancelled': 'Cancelado'
                        }[paymentStatus] || 'Desconhecido'

                        return (
                            <div key={booking.id} className="glass-card p-6 flex flex-col gap-6 group hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
                                <div className="absolute -right-4 -top-4 w-28 h-28 bg-primary/5 rounded-full blur-2xl group-hover:bg-primary/10 transition-colors" />

                                {/* Header */}
                                <div className="relative z-10 flex items-start justify-between">
                                    <div className="flex items-start space-x-4">
                                        <div className="w-14 h-14 rounded-xl bg-secondary/10 flex items-center justify-center text-secondary border-2 border-secondary/20">
                                            <CreditCard className="w-6 h-6" />
                                        </div>
                                        <div className="space-y-2">
                                            <h3 className="text-2xl font-black text-primary uppercase tracking-tight">R$ {booking.totalPrice?.toFixed(2)}</h3>
                                            <div className="flex flex-wrap gap-2">
                                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${statusColor}`}>
                                                    {statusText}
                                                </span>
                                                <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                                                    {providerName}
                                                </span>
                                                <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-800">
                                                    {methodName}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Details Grid */}
                                <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                                    <div>
                                        <p className="text-gray-400 font-bold uppercase tracking-wider mb-1">Membro</p>
                                        <p className="text-gray-900 font-semibold">{(booking as any).userName || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-400 font-bold uppercase tracking-wider mb-1">Espaço</p>
                                        <p className="text-gray-900 font-semibold">{(booking as any).spaceName || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-400 font-bold uppercase tracking-wider mb-1">Data/Hora</p>
                                        <p className="text-gray-900 font-semibold">{updatedAt.toLocaleString('pt-BR')}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-400 font-bold uppercase tracking-wider mb-1">ID Transação</p>
                                        <p className="text-gray-900 font-semibold truncate" title={transactionId}>
                                            {transactionId.length > 15 ? `${transactionId.substring(0, 15)}...` : transactionId}
                                        </p>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="relative z-10 flex items-center justify-end space-x-3">
                                    {booking.payment?.receiptUrl && (
                                        <button
                                            onClick={() => setSelectedReceipt(booking.payment?.receiptUrl || null)}
                                            className="flex items-center space-x-2 px-4 py-2 bg-white text-primary/60 font-bold rounded-xl text-xs uppercase tracking-wider hover:bg-primary/5 transition-all border-2 border-slate-200"
                                        >
                                            <Eye className="w-4 h-4" />
                                            <span>Ver Comprovante</span>
                                        </button>
                                    )}

                                    {/* Só mostrar ações se for PIX manual pendente */}
                                    {paymentProvider === 'pix_manual' && paymentStatus === 'pending' && (
                                        <>
                                            <button
                                                onClick={() => handleAction(booking.id, 'CANCELED')}
                                                className="p-3 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all"
                                                title="Rejeitar"
                                            >
                                                <X className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={() => handleAction(booking.id, 'CONFIRMED')}
                                                className="px-6 py-3 bg-primary-600 text-white font-bold rounded-xl text-xs uppercase tracking-wider shadow-lg shadow-primary-200 hover:bg-primary-700 transition-all flex items-center space-x-2"
                                            >
                                                <Check className="w-4 h-4" />
                                                <span>Confirmar</span>
                                            </button>
                                        </>
                                    )}

                                    {/* Para pagamentos automáticos, apenas mostrar status */}
                                    {(paymentProvider === 'mercadopago' || paymentProvider === 'infinitypay') && (
                                        <div className="px-4 py-2 bg-gray-50 rounded-xl">
                                            <p className="text-xs font-bold text-gray-600">
                                                {paymentStatus === 'approved' ? '✓ Aprovado automaticamente' : '⏳ Processando...'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            {/* Modal Simples de Comprovante */}
            {selectedReceipt && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
                    <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setSelectedReceipt(null)}></div>
                    <div className="relative bg-white rounded-[3rem] p-4 max-w-2xl w-full animate-in zoom-in-95 duration-300">
                        <button
                            onClick={() => setSelectedReceipt(null)}
                            className="absolute -top-4 -right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-xl text-gray-400 hover:text-gray-900"
                        >
                            <X className="w-6 h-6" />
                        </button>
                        <img src={selectedReceipt} className="w-full rounded-[2.5rem] shadow-inner" alt="Comprovante" />
                    </div>
                </div>
            )}

            <div className="bg-blue-50/50 p-6 rounded-3xl border border-blue-100 flex items-start space-x-4">
                <Info className="w-6 h-6 text-blue-600 shrink-0" />
                <div className="space-y-1">
                    <h4 className="text-[10px] font-black text-blue-900 uppercase tracking-widest">Dica Administrativa</h4>
                    <p className="text-[10px] text-blue-800 font-medium leading-relaxed">
                        Sempre confira os dados do comprovante (nome, CPF e valor) no seu aplicativo do banco antes de confirmar.
                        Uma vez confirmado, a reserva passa para o status "Confirmado" e o usuário recebe a notificação.
                    </p>
                </div>
            </div>
        </div>
    )

    if (embedded) {
        return Content
    }

    return Content
}
